/**
 * Core types for the tournament format system
 */

import { isPresent } from "@mixitone/util";

/**
 * Unique identifier for an entry
 */
export type EntryId = string;

/**
 * Entry with seed information and optional metadata
 * [id, seed, metadata?]
 */
export type Entry = [EntryId, number, Record<string, any>?];

/**
 * Match entry with win status
 * [id, isWinner]
 */
export type MatchEntry = [EntryId, boolean];

/**
 * Match representation
 * [entry1, entry2, metadata?]
 * Each entry can be null if not yet assigned
 */
export type Match = [MatchEntry | null, MatchEntry | null, Record<string, any>?];

/**
 * Round of matches
 */
export type Round = Match[];

export type Winner = [EntryId, Record<string, any>];

/**
 * Complete tournament state
 *
 * The state can optionally include a winners property that contains
 * an array of EntryIds representing the winners of the tournament
 */
export type TournamentState = {
  entries: Entry[];
  rounds: Round[];
  winners?: Winner[];
  metadata?: Record<string, any>;
};

export function blankTournamentState(): TournamentState {
  return { entries: [], rounds: [], metadata: {} };
}

export function isMatch(ary: [] | Entry | EntryId | Match): ary is Match {
  return (
    Array.isArray(ary) &&
    ary.length >= 2 &&
    ary.length <= 3 &&
    (ary[0] === null || (Array.isArray(ary[0]) && ary[0].length === 2)) &&
    (ary[1] === null || (Array.isArray(ary[1]) && ary[1].length === 2))
  );
}

export function isEntry(ary: [] | Entry | EntryId | Match): ary is Entry {
  return (
    Array.isArray(ary) &&
    ary.length >= 2 &&
    ary.length <= 3 &&
    typeof ary[0] === "string" &&
    typeof ary[1] === "number"
  );
}

export function isEntryId(ary: [] | Entry | EntryId | Match): ary is EntryId {
  return typeof ary === "string";
}

export function isRound(ary: null | undefined | Round | Entry[] | EntryId[]): ary is Round {
  return Array.isArray(ary) && ary.length > 0 && isMatch(ary[0]);
}

export function matchEntries(match: Match): MatchEntry[] {
  return [match[0], match[1]].filter(isPresent);
}

export function matchKey(match: Match): string {
  return matchEntries(match)
    .map((entry) => entry[0])
    .sort((a, b) => a.localeCompare(b))
    .join("-");
}

export function matchComplete(match: Match): boolean {
  return matchWinnerId(match) !== undefined;
}

/** Match was voided (no winner, no stand-down, still terminal for group completion). */
export function matchVoided(match: Match): boolean {
  return match[2]?.status === "voided";
}

/** Match is finished for scheduling: has a winner or was voided. */
export function matchResolved(match: Match): boolean {
  return matchComplete(match) || matchVoided(match);
}

export function matchWinnerId(match: Match): EntryId | undefined {
  if (match[0]?.[1]) {
    return match[0]?.[0];
  }
  if (match[1]?.[1]) {
    return match[1]?.[0];
  }
}

export function matchLoserId(match: Match): EntryId | undefined {
  if (match[0]?.[1]) {
    return match[1]?.[0];
  }
  if (match[1]?.[1]) {
    return match[0]?.[0];
  }
}

export function matchHasEntries(match: Match): boolean {
  return isPresent(match[0]) && isPresent(match[1]);
}

export function matchMetadata(match: Match): Record<string, any> | undefined {
  return match[2];
}
